import React, { useState } from 'react';
import clsx from 'clsx';

export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked,
      defaultChecked = false,
      onCheckedChange,
      disabled = false,
      size = 'md',
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    const isControlled = checked !== undefined;
    const [internalChecked, setInternalChecked] = useState(defaultChecked);
    const currentChecked = isControlled ? checked : internalChecked;

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (event.defaultPrevented || disabled) return;
      const next = !currentChecked;
      if (!isControlled) {
        setInternalChecked(next);
      }
      onCheckedChange?.(next);
    };

    const sizeStyles = {
      sm: { track: 'h-5 w-9', thumb: 'h-3 w-3', translate: 'translate-x-4' },
      md: { track: 'h-6 w-11', thumb: 'h-4 w-4', translate: 'translate-x-5' },
    }[size];

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={currentChecked}
        aria-disabled={disabled}
        onClick={handleToggle}
        disabled={disabled}
        className={clsx(
          'relative inline-flex items-center rounded-full border-2 transition-all duration-150',
          sizeStyles.track,
          currentChecked ? 'bg-emerald-500 border-black' : 'bg-slate-200 border-black',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500',
          !disabled && 'cursor-pointer',
          disabled && 'opacity-60 cursor-not-allowed',
          '[.theme-liquid-glass_&]:border-white/20',
          currentChecked
            ? '[.theme-liquid-glass_&]:bg-violet-500/80'
            : '[.theme-liquid-glass_&]:bg-white/10',
          '[.theme-liquid-glass_&]:focus-visible:ring-violet-400 [.theme-liquid-glass_&]:focus-visible:ring-offset-slate-900',
          className
        )}
        {...props}
      >
        <span
          className={clsx(
            'inline-block rounded-full border-2 bg-white transition-transform duration-150',
            sizeStyles.thumb,
            currentChecked ? sizeStyles.translate : 'translate-x-0',
            'border-black',
            '[.theme-liquid-glass_&]:border-white/30'
          )}
        />
      </button>
    );
  }
);

Switch.displayName = 'Switch';
